<template>
  <div
    class="card-tarja-danca-huck"
    :class="{
      'card-tarja-danca-huck--fullscreen': isFullScreen,
      'card-tarja-danca-huck--media': isMediaLayout,
    }"
    :style="cardStyle"
    :key="displayedItem.id_colaboracao"
  >
    <div
      class="danca-huck-layout"
      :class="{ 'danca-huck-layout--media': isMediaLayout }"
    >
      <div
        v-if="!hideUserInfo"
        class="danca-huck-layout__title-group"
        :class="blockAnimationClass"
        :style="{ fontSize: titleFontSize }"
      >
        <div class="danca-huck-layout__bar danca-huck-layout__bar--title">
          <video
            class="danca-huck-layout__background"
            :src="titleBackground"
            autoplay
            muted
            loop
            playsinline
            preload="auto"
            aria-hidden="true"
          ></video>
        </div>
        <div class="danca-huck-layout__title">
          <img
            class="danca-huck-layout__avatar"
            :src="avatarUrl"
            :alt="`Avatar de ${headerText}`"
            @error="avatarLoadFailed = true"
          />
          <span>{{ headerText }}</span>
        </div>
      </div>
      <div
        class="danca-huck-layout__content-area"
        :class="[
          blockAnimationClass,
          { 'danca-huck-layout__content-area--media': isMediaLayout },
        ]"
      >
        <div class="danca-huck-layout__bar danca-huck-layout__bar--content">
          <video
            class="danca-huck-layout__background"
            :src="contentBackground"
            autoplay
            muted
            loop
            playsinline
            preload="auto"
            aria-hidden="true"
          ></video>
        </div>
        <div
          class="danca-huck-layout__content"
          :class="{
            'danca-huck-layout__content--media': isVisualMedia,
            'danca-huck-layout__content--audio': isAudio,
            'danca-huck-layout__content--media-with-text':
              isMediaLayout && hasText,
          }"
          :style="{ fontSize: contentFontSize }"
        >
          <span
            v-if="isMediaLayout && hasText"
            class="danca-huck-layout__media-text"
          >{{ displayedItem.dsc_texto_colaboracao }}</span>
          <template v-if="isVisualMedia">
            <img
              v-if="displayedItem.dsc_tipo_midia_colaboracao === 'image'"
              :src="displayedItem.url_midia_colaboracao"
              :style="mediaBoundsStyle"
              alt="Mídia da colaboração"
            />
            <video
              v-else
              :key="displayedItem.url_midia_colaboracao"
              :src="displayedItem.url_midia_colaboracao"
              :style="mediaBoundsStyle"
              autoplay
              muted
              playsinline
            ></video>
          </template>
          <audio
            v-else-if="isAudio"
            :key="displayedItem.url_midia_colaboracao"
            controls
            controlslist="nodownload"
            autoplay
          >
            <source :src="displayedItem.url_midia_colaboracao" />
          </audio>
          <span v-else>{{ displayedItem.dsc_texto_colaboracao || "" }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import titleBackground from "@/assets/DANCA_HUCK/ROXO_1.webm";
import contentBackground from "@/assets/DANCA_HUCK/AZUL_2.webm";
import defaultUserAvatar from "@/assets/User.png";

// Durações das animações de entrada/saída das tarjas (referência
// DANCA_HUCK). Devem casar com --tarja-anim-enter/--tarja-anim-leave no CSS
// e com a duração de leave do <transition name="danca-huck-tarja"> no
// TarjaView.
const ENTER_DURATION_MS = 700;
const LEAVE_DURATION_MS = 600;

export default {
  name: "CardTarjaDancaHuck",
  props: {
    item: {
      type: Object,
      required: true,
    },
    posicaoCard: {
      type: String,
      default: "esquerda",
    },
    isFullScreen: Boolean,
    hideUserInfo: Boolean,
    flgExibirUf: Boolean,
  },
  data() {
    return {
      titleBackground,
      contentBackground,
      avatarLoadFailed: false,
      midiaOrientation: null,
      // Item efetivamente exibido. Na troca de colaboração a tarja atual
      // sai animada (colapsa à direita) e só depois exibe o próximo item.
      displayedItem: this.item,
      // Fase da animação dos blocos: "entering" | "shown" | "leaving".
      animationPhase: "entering",
      pendingItem: null,
      animationTimer: null,
      // Redimensionamento com mídia igual ao CardTarjaPanelaSportv:
      // porcentagem da tela que o card e a mídia podem ocupar.
      percentScreenDim: {
        maxWidth: 30,
        minWidth: 20,
        minHeight: 40,
        maxHeight: 70,
        fullScreenMaxWidth: 100,
        fullScreenMaxHeight: 100,
        fullScreenMinWidth: 100,
        fullScreenMinHeight: 100,
      },
    };
  },
  created() {
    this.playEnter();
    if (this.displayedItem.url_midia_colaboracao) {
      this.getMidiaOrientation();
    }
  },
  beforeDestroy() {
    this.clearAnimationTimer();
  },
  computed: {
    hasMedia() {
      return Boolean(this.displayedItem.url_midia_colaboracao);
    },
    // Texto da colaboração acompanhando a mídia: exibido acima dela na
    // tarja de conteúdo.
    hasText() {
      return Boolean(
        this.displayedItem.dsc_texto_colaboracao &&
          this.displayedItem.dsc_texto_colaboracao.trim()
      );
    },
    isVisualMedia() {
      return (
        this.hasMedia &&
        ["image", "video"].includes(this.displayedItem.dsc_tipo_midia_colaboracao)
      );
    },
    // Imagem/vídeo/áudio usam o layout em coluna proporcional ao card;
    // só o texto mantém a tarja 16/9 original.
    isMediaLayout() {
      return this.isVisualMedia || this.isAudio;
    },
    isAudio() {
      return (
        this.hasMedia && this.displayedItem.dsc_tipo_midia_colaboracao === "audio"
      );
    },
    // Classe de animação aplicada aos blocos de título e conteúdo conforme
    // a fase atual: entrada expande da esquerda, saída colapsa à direita.
    blockAnimationClass() {
      if (this.animationPhase === "entering") {
        return "danca-huck-block--entering";
      }
      if (this.animationPhase === "leaving") {
        return "danca-huck-block--leaving";
      }
      return null;
    },
    screenDimensions() {
      return {
        width: this.$vuetify.breakpoint.width,
        height: this.$vuetify.breakpoint.height,
      };
    },
    // Limites da mídia (imagem/vídeo), mesmos cálculos do CardTarjaPanelaSportv.
    midiaSize() {
      const { width, height } = this.screenDimensions;
      const { maxWidth, maxHeight } = this.percentScreenDim;
      if (this.isFullScreen) {
        return { width: width - 20, height: height - 100 };
      }
      if (this.midiaOrientation === "vertical") {
        return {
          width: (maxWidth * width) / 100 - 20,
          height: (maxHeight * height) / 100,
        };
      }
      return {
        width: (maxWidth * width) / 100 - 20,
        height: (maxHeight * height) / 100 - 100,
      };
    },
    mediaBoundsStyle() {
      return {
        maxWidth: `${this.midiaSize.width}px`,
        maxHeight: `${this.midiaSize.height}px`,
      };
    },
    avatarUrl() {
      if (
        this.avatarLoadFailed ||
        this.displayedItem.flg_sem_avatar !== "N" ||
        !this.displayedItem.url_avatar_colaborador
      ) {
        return defaultUserAvatar;
      }
      return this.displayedItem.url_avatar_colaborador;
    },
    headerText() {
      if (this.flgExibirUf && this.displayedItem.dsc_uf_colaborador) {
        return `${this.displayedItem.dsc_nome_colaborador} - ${this.displayedItem.dsc_uf_colaborador}`;
      }
      return this.displayedItem.dsc_nome_colaborador || "";
    },
    titleFontSize() {
      const titleLength = this.headerText.trim().length;
      if (titleLength > 32) return "clamp(0.75rem, 1.75vw, 25px)";
      if (titleLength > 20) return "clamp(0.85rem, 2vw, 28px)";
      return "clamp(0.95rem, 2.25vw, 32px)";
    },
    contentFontSize() {
      // Mesma estratégia do título: a fonte encolhe conforme o texto cresce.
      // A tarja de conteúdo já cresce para baixo (content-area com altura
      // automática) até o limite do card; acima de ~12 linhas na fonte
      // cheia, reduzir a fonte mantém todas as linhas visíveis.
      const textLength = (
        this.displayedItem.dsc_texto_colaboracao || ""
      ).trim().length;
      if (textLength > 600) return "clamp(0.65rem, 1.3vw, 19px)";
      if (textLength > 360) return "clamp(0.75rem, 1.6vw, 23px)";
      return "clamp(0.8rem, 2vw, 29px)";
    },
    cardStyle() {
      if (this.isFullScreen) {
        if (this.hasMedia) {
          const { width, height } = this.screenDimensions;
          const {
            fullScreenMaxWidth,
            fullScreenMaxHeight,
            fullScreenMinWidth,
            fullScreenMinHeight,
          } = this.percentScreenDim;
          return {
            justifySelf: "center",
            maxWidth: `${(fullScreenMaxWidth * width) / 100}px !important`,
            maxHeight: `${(fullScreenMaxHeight * height) / 100}px !important`,
            minWidth: `${(fullScreenMinWidth * width) / 100}px !important`,
            minHeight: `${(fullScreenMinHeight * height) / 100}px !important`,
          };
        }
        return {
          width: "min(100vw, calc(100vh * 16 / 9))",
        };
      }

      const positionStyles = {
        esquerda: {
          justifySelf: "start",
          marginLeft: "0",
        },
        centro: {
          justifySelf: "center",
        },
        direita: {
          justifySelf: "end",
          marginRight: "0",
        },
      };

      if (!this.hasMedia) {
        return positionStyles[this.posicaoCard] || positionStyles.esquerda;
      }

      // Com mídia (imagem/vídeo/áudio), o posicionamento segue as mesmas
      // regras do CardTarjaPanelaSportv. Sem max-height no card: a mídia
      // já é limitada por midiaSize; um cap aqui fazia título + mídia +
      // paddings transbordarem o card para baixo e a tarja colava na
      // borda inferior da tela. A margem inferior garante o respiro
      // fixo de 10px da borda (o wrapper do TarjaView alinha ao fim).
      const { width } = this.screenDimensions;
      const { maxWidth } = this.percentScreenDim;
      const mediaPositionStyles = {
        esquerda: {
          justifySelf: "flex-start",
          marginLeft: "120px",
        },
        centro: {
          justifySelf: "center",
        },
        direita: {
          justifySelf: "flex-start",
          marginRight: "60px",
        },
      };
      const position =
        mediaPositionStyles[this.posicaoCard] || mediaPositionStyles.esquerda;

      if (this.isAudio) {
        return {
          ...position,
          maxWidth: `${Math.min(640, (maxWidth * width) / 100)}px`,
          marginBottom: "10px",
        };
      }

      return {
        ...position,
        maxWidth: `${(maxWidth * width) / 100}px`,
        marginBottom: "10px",
      };
    },
  },
  methods: {
    getMidiaOrientation() {
      if (this.displayedItem.dsc_tipo_midia_colaboracao === "image") {
        const image = new Image();
        image.src = this.displayedItem.url_midia_colaboracao;
        image.onload = () => {
          this.midiaOrientation =
            image.height > image.width ? "vertical" : "horizontal";
        };
      } else if (this.displayedItem.dsc_tipo_midia_colaboracao === "video") {
        const createdVideoElement = document.createElement("video");
        createdVideoElement.src = this.displayedItem.url_midia_colaboracao;
        createdVideoElement.style.visibility = "hidden";
        createdVideoElement.style.position = "absolute";
        createdVideoElement.style.width = "0";
        createdVideoElement.style.height = "0";
        document.body.appendChild(createdVideoElement);
        createdVideoElement.addEventListener("loadedmetadata", () => {
          this.midiaOrientation =
            createdVideoElement.videoHeight > createdVideoElement.videoWidth
              ? "vertical"
              : "horizontal";
          document.body.removeChild(createdVideoElement);
        });
      }
    },
    // Entrada: blocos expandem da esquerda (scaleX 0→1). Ao final, se uma
    // nova colaboração chegou durante a entrada, encerra este card para
    // exibi-la.
    playEnter() {
      this.animationPhase = "entering";
      this.setAnimationTimer(() => {
        this.animationPhase = "shown";
        if (this.pendingItem) {
          this.startLeave();
        }
      }, ENTER_DURATION_MS);
    },
    // Saída: blocos colapsam em direção à direita (scaleX 1→0) e só então o
    // item pendente (o mais recente recebido) passa a ser exibido.
    startLeave() {
      this.animationPhase = "leaving";
      this.setAnimationTimer(() => {
        const nextItem = this.pendingItem;
        this.pendingItem = null;
        this.displayedItem = nextItem;
        this.avatarLoadFailed = false;
        this.midiaOrientation = null;
        if (nextItem.url_midia_colaboracao) {
          this.getMidiaOrientation();
        }
        this.playEnter();
      }, LEAVE_DURATION_MS);
    },
    setAnimationTimer(callback, durationMs) {
      this.clearAnimationTimer();
      this.animationTimer = setTimeout(callback, durationMs);
    },
    clearAnimationTimer() {
      if (this.animationTimer) {
        clearTimeout(this.animationTimer);
        this.animationTimer = null;
      }
    },
  },
  watch: {
    // O TarjaView reaproveita esta instância entre colaborações (o card é
    // keyed por produção, não por colaboração). Ao trocar o item, a tarja
    // atual sai animada e só então o novo entra; atualizações do mesmo
    // item aparecem sem animação.
    item(newItem) {
      if (!newItem || !newItem.id_colaboracao) {
        return;
      }
      if (newItem.id_colaboracao === this.displayedItem.id_colaboracao) {
        this.displayedItem = newItem;
        return;
      }
      this.pendingItem = newItem;
      if (this.animationPhase === "shown") {
        this.startLeave();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "Gotham Bold";
  src: url("@/assets/DANCA_HUCK/FONTE/Gotham/Gotham-Bold.ttf") format("truetype");
  font-weight: 700;
  font-style: normal;
  font-display: swap;
}

.card-tarja-danca-huck {
  position: relative;
  width: min(100vw, 1440px);
  aspect-ratio: 16 / 9;
  overflow: hidden;
  // Sem fundo: apenas as tarjas internas aparecem (referência DANCA_HUCK).
  // Raio curto fixo (~26px num card de 1440px), independente da altura do
  // conteúdo da tarja.
  --tarja-bar-radius: min(1.8vw, 26px);
  // Respiro interno das tarjas medido no mock de referência (1567×881):
  // título com ~30% da altura da tarja acima/abaixo do conteúdo e ~28% nas
  // laterais; conteúdo com ~15% da altura. Valores em px = card cheio (1440).
  --tarja-title-pad-y: min(1.6vw, 23px);
  --tarja-content-pad-y: min(1.25vw, 18px);
  --tarja-pad-x: min(1.5vw, 21px);
  // Durações das animações de entrada/saída dos blocos (referência
  // DANCA_HUCK). Casam com ENTER_DURATION_MS/LEAVE_DURATION_MS no script.
  --tarja-anim-enter: 0.7s;
  --tarja-anim-leave: 0.6s;
}

.card-tarja-danca-huck--media {
  aspect-ratio: auto;
  height: auto;
  overflow: visible;
  // Card de mídia ocupa só 20–30% da tela: raio proporcional ao card.
  --tarja-bar-radius: 0.6vw;
}

.card-tarja-danca-huck--fullscreen {
  flex: none;
  max-height: 100vh;
}

.card-tarja-danca-huck--fullscreen.card-tarja-danca-huck--media {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  // Em tela cheia o card volta à escala cheia da tela.
  --tarja-bar-radius: min(1.8vw, 26px);
}

.danca-huck-layout {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.danca-huck-layout__bar {
  position: absolute;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.75);
  border-radius: var(--tarja-bar-radius, 26px);
  box-sizing: border-box;
  box-shadow: 0 0 4px rgba(255, 255, 255, 0.35);
  pointer-events: none;
}

.danca-huck-layout__title-group {
  position: absolute;
  top: 36.5%;
  left: 20%;
  z-index: 3;
  // O título nunca ultrapassa 70% da largura da tarja de conteúdo
  // (64% do card): 0,7 × 64% = 44,8%.
  width: 44.8%;
  height: 9.5%;
}

.danca-huck-layout__bar--title {
  inset: 0;
  width: 100%;
  height: 100%;
}

.danca-huck-layout__background {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
  object-fit: cover;
}

.danca-huck-layout__title,
.danca-huck-layout__content {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
  padding: 0 var(--tarja-pad-x);
  color: #fff;
  font-family: "Gotham Bold", sans-serif;
  font-weight: 700;
  line-height: 1.1;
  text-align: left;
  text-transform: uppercase;
}

.danca-huck-layout__title {
  width: 100%;
  height: 100%;
  gap: 1.5%;
  white-space: nowrap;
}

// App.vue pinta globalmente `p, span, label` com var(--v-text_secondary-base)
// (cinza ~rgb(161,161,163) no tema dark). Essa declaração casa direto no span
// e vence a cor #fff herdada dos blocos de título/conteúdo; `inherit` restaura
// o branco puro definido nos blocos pais (especificidade 0,2,1 > 0,0,1).
.danca-huck-layout__title span,
.danca-huck-layout__content span {
  color: inherit;
}

.danca-huck-layout__title span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.danca-huck-layout__avatar {
  flex: none;
  width: min(5.5vw, 48px);
  height: min(5.5vw, 48px);
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  object-fit: cover;
}

.danca-huck-layout__content-area {
  position: absolute;
  top: 44.5%;
  left: 20%;
  z-index: 2;
  width: 64%;
  // Altura automática com piso: com texto curto a tarja mantém a altura da
  // referência (14,5%); com texto longo cresce para baixo, dentro do espaço
  // livre do card, em vez de cortar as linhas excedentes.
  height: auto;
  min-height: 14.5%;
  // Flex + stretch: o bloco de texto preenche a altura da tarja (centraliza
  // o texto curto no piso de 14,5% e acompanha o crescimento com texto longo).
  display: flex;
  align-items: stretch;
  // Piso de largura da tarja de conteúdo em qualquer layout/tamanho.
  min-width: 600px;
}

.danca-huck-layout__bar--content {
  inset: 0;
  width: 100%;
  height: 100%;
}

.danca-huck-layout__content {
  position: relative;
  z-index: 3;
  width: 100%;
  // Altura via stretch do content-area: preenche o piso de 14,5% com texto
  // curto (texto centrado) e cresce junto com o texto longo.
  white-space: normal;
  word-break: break-word;
  // Respiro vertical do texto da tarja de conteúdo (referência: ~18px num
  // card de 1440). Com texto em duas linhas garante folga nas bordas; no
  // layout de mídia (altura automática) cria o espaço entre a mídia e a
  // borda da tarja.
  padding-block: var(--tarja-content-pad-y);
}

.danca-huck-layout__content--media {
  justify-content: center;
}

.danca-huck-layout__content--media img,
.danca-huck-layout__content--media video {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.danca-huck-layout__content--audio {
  justify-content: center;
}

.danca-huck-layout__content audio {
  width: 100%;
  max-width: 100%;
}

// Mídia + texto: o texto da colaboração ocupa a linha acima da mídia
// (imagem/vídeo) ou do player de áudio, dentro da mesma tarja de
// conteúdo; a mídia continua centrada.
.danca-huck-layout__content--media-with-text {
  flex-direction: column;
  justify-content: center;
  gap: min(0.5vw, 8px);
}

.danca-huck-layout__content--media-with-text .danca-huck-layout__media-text {
  align-self: stretch;
  text-align: left;
}

// Mídia (imagem/vídeo): layout em coluna proporcional ao card. O card é
// limitado por percentual da tela (cardStyle), então os elementos internos
// acompanham o tamanho do card em vez de usar offsets fixos de viewport.
.danca-huck-layout--media {
  height: auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  padding: 1.5% 0;
  overflow: visible;
}

.danca-huck-layout--media .danca-huck-layout__title-group {
  position: relative;
  top: auto;
  left: auto;
  // Mesma regra do layout fixo: no máximo 70% da tarja de conteúdo (que
  // aqui ocupa 100% da largura), alinhada à esquerda com ela.
  width: 70%;
  align-self: flex-start;
  height: auto;
}

.danca-huck-layout--media .danca-huck-layout__title {
  height: auto;
  // Altura automática: o padding vertical efetivamente cresce a tarja e dá
  // o respiro do avatar/nome igual à referência (~30% da altura da tarja).
  padding-block: var(--tarja-title-pad-y);
}

.danca-huck-layout--media .danca-huck-layout__content-area {
  position: relative;
  top: auto;
  left: auto;
  width: 100%;
  height: auto;
  min-height: 0;
  // Na referência a tarja de conteúdo fica atrás da tarja de título
  // (sobrepõem ~1%), sem espaçamento entre elas.
  margin-top: -1%;
}

.danca-huck-layout--media .danca-huck-layout__content--media img,
.danca-huck-layout--media .danca-huck-layout__content--media video {
  max-width: 100%;
}

.card-tarja-danca-huck--fullscreen.card-tarja-danca-huck--media
  .danca-huck-layout {
  flex: none;
  width: 100%;
}

// Animações de entrada/saída dos blocos de título e conteúdo (referência
// DANCA_HUCK). Entrada (~0,7s, ease-out): o bloco nasce com largura zero na
// borda esquerda (transform-origin: left center) e expande até a largura
// final. Saída (~0,6s, ease-in): o bloco colapsa horizontalmente em direção
// à borda direita (transform-origin: right center) até desaparecer. Sem
// fade: apenas a geometria (scaleX) é animada.
.danca-huck-block--entering {
  transform-origin: left center;
  animation: danca-huck-block-in var(--tarja-anim-enter)
    cubic-bezier(0.25, 1, 0.5, 1) both;
}

.danca-huck-block--leaving {
  transform-origin: right center;
  animation: danca-huck-block-out var(--tarja-anim-leave)
    cubic-bezier(0.5, 0, 0.75, 0) both;
}

// Saída por desmontagem (TarjaView remove o card via
// <transition name="danca-huck-tarja">): as classes de leave chegam pela
// raiz externa e disparam a mesma contração nos dois blocos.
.danca-huck-tarja-leave-active .danca-huck-layout__title-group,
.danca-huck-tarja-leave-active .danca-huck-layout__content-area {
  transform-origin: right center;
  animation: danca-huck-block-out var(--tarja-anim-leave)
    cubic-bezier(0.5, 0, 0.75, 0) both;
}

@keyframes danca-huck-block-in {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

@keyframes danca-huck-block-out {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

@media (max-width: 600px) {
  .card-tarja-danca-huck {
    width: 100vw;
  }

  .danca-huck-layout__title,
  .danca-huck-layout__content {
    padding-right: 1%;
    padding-left: 1%;
  }
}
</style>
